import { useState } from "react";
import { Mail, MapPin, Phone, Instagram, Facebook, Send } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Your message is on its way ♡", {
        description: "We'll reply within 24 hours, softly.",
      });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pb-24">
      <section className="relative py-20 md:py-28 bg-gradient-to-b from-blush-50 via-cream-100 to-cream-100 overflow-hidden">
        <div className="blob bg-blush-200 w-[420px] h-[420px] -top-24 -right-20" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="font-script text-3xl text-blush-400">say hello</span>
          <h1 className="font-heading text-4xl md:text-6xl text-ink-900 mt-1">
            We'd love to hear from you
          </h1>
          <p className="mt-4 font-body text-ink-700 max-w-xl mx-auto">
            Whether it's a styling question, a custom request, or a quiet compliment — our
            atelier inbox is always open.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 space-y-8">
          {[
            {
              Icon: Mail,
              title: "Email",
              body: "hello@flownte.in",
              sub: "Replies within 24 hours",
            },
            {
              Icon: Phone,
              title: "Phone",
              body: "+91 60025 81586",
              sub: "Mon – Sat, 10am – 7pm IST",
            },
            {
              Icon: MapPin,
              title: "Atelier",
              body: "Guwahati, Assam",
              sub: "India · By appointment",
            },
          ].map((c) => (
            <div
              key={c.title}
              data-testid={`contact-info-${c.title.toLowerCase()}`}
              className="flex items-start gap-4 rounded-3xl border border-blush-100 bg-white p-6"
            >
              <div className="h-12 w-12 rounded-full bg-blush-100 grid place-items-center text-blush-600 shrink-0">
                <c.Icon size={18} strokeWidth={1.4} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-ink-500 font-body">
                  {c.title}
                </p>
                <p className="mt-1 font-heading text-xl text-ink-900">{c.body}</p>
                <p className="mt-1 font-body text-sm text-ink-500">{c.sub}</p>
              </div>
            </div>
          ))}

          <div className="flex items-center gap-3">
            <a
              href="https://instagram.com"
              target="_blank"
              rel="noreferrer"
              className="h-11 w-11 rounded-full grid place-items-center bg-white border border-blush-100 text-blush-600 hover:bg-blush-50 transition"
            >
              <Instagram size={16} strokeWidth={1.5} />
            </a>
            <a
              href="https://facebook.com"
              target="_blank"
              rel="noreferrer"
              className="h-11 w-11 rounded-full grid place-items-center bg-white border border-blush-100 text-blush-600 hover:bg-blush-50 transition"
            >
              <Facebook size={16} strokeWidth={1.5} />
            </a>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="lg:col-span-7 rounded-[2rem] bg-white border border-blush-100 p-8 md:p-10 shadow-sm"
        >
          <h2 className="font-heading text-3xl text-ink-900">Send a message</h2>
          <p className="mt-2 font-body text-ink-500">
            Tell us a little about what you're looking for.
          </p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">
                Your name
              </label>
              <input
                required
                value={form.name}
                onChange={onChange("name")}
                className="field mt-2"
                placeholder="Anaya Kapoor"
                data-testid="contact-name"
              />
            </div>
            <div>
              <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">
                Your email
              </label>
              <input
                required
                type="email"
                value={form.email}
                onChange={onChange("email")}
                className="field mt-2"
                placeholder="you@email.com"
                data-testid="contact-email"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">
              Subject
            </label>
            <input
              required
              value={form.subject}
              onChange={onChange("subject")}
              className="field mt-2"
              placeholder="Question about the Rani Velvet Kurti"
              data-testid="contact-subject"
            />
          </div>

          <div className="mt-4">
            <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">
              Message
            </label>
            <textarea
              required
              rows={5}
              value={form.message}
              onChange={onChange("message")}
              className="field-area mt-2 resize-none"
              placeholder="Write to us, softly..."
              data-testid="contact-message"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="btn-maroon mt-8 inline-flex items-center gap-2 disabled:opacity-60"
          >
            {loading ? "Sending..." : "Send message"}
            <Send size={15} strokeWidth={1.5} />
          </button>
        </form>
      </section>
    </div>
  );
}
